import * as React from 'react';
import { isDesktop } from '@westwing/ui-kit/breakPoints';
import useFeatureToggle from '../useFeatureToggle';
import { getPageTypeByUrl } from '../UrlManager/pageTypeService';
import { PageType } from '../UrlManager/types';
import { SMALL_APP_BANNER_SHOWN_IN_SESSION } from 'OneHeader/Banners/AppBanner/useAppBanner';
import useRouter from 'Client/hooks/useRouter';
import { sendCustomEventReadyForOverlays } from 'Client/hooks/Braze/brazeEventService';
import { useBraze } from 'Client/hooks/Braze/useBraze';
import { useBellaSelector } from 'Client/redux/hooks';
import useBellaCookies, { oneHourTTL } from 'Client/hooks/useBellaCookies';
import { SHOP_TO_CLUB_OVERLAY_SHOWN_IN_THIS_SESSION } from 'Client/components/Overlays/ShopToClub/hooks/useClubRegistrationPopup';
import { AppBannerValues } from 'Common/appshell_types';
import { useLogger } from 'Client/logger/LoggerProvider';

export const SENT_BRAZE_READY_EVENT = 'ww_sent_braze_ready_event';

export const shouldSendBrazeReadyEvent = (
    {
        cookiesEnabled,
        isLoggedIn,
        braze,
        shopToClubOverlayShownInThisSession,
        sentBrazeReadyEvent,
        smallAppBannerShownInThisSession,
        isCartPage,
        isWebview,
    }: {
        cookiesEnabled: boolean;
        isLoggedIn: boolean;
        braze: boolean;
        shopToClubOverlayShownInThisSession: boolean;
        sentBrazeReadyEvent: boolean;
        smallAppBannerShownInThisSession: boolean;
        isCartPage: boolean;
        isWebview: boolean;
    },
    log: (msg: string) => void
) => {
    if (isWebview) {
        log("don't send in webviews");
        return false;
    }
    if (isCartPage) {
        log("don't send in cart");
        return false;
    }
    if (!cookiesEnabled) {
        log('this feature only works with cookies, returning early');
        return false;
    }

    if (!isLoggedIn) {
        log('user is not logged, returning early');
        return false;
    }

    if (!braze) {
        log('braze client not present, returning early');
        return false;
    }

    if (shopToClubOverlayShownInThisSession) {
        log('user saw shop to club overlay, returning early');
        return false;
    }

    if (sentBrazeReadyEvent) {
        log('we already sent the ready event, returning early');
        return false;
    }

    if (smallAppBannerShownInThisSession) {
        log('user saw small app banner, returning early');
        return false;
    }
    return true;
};

const useBrazeReady = () => {
    const isLoggedIn = useBellaSelector(state => state.appShellLogin.isLoggedIn);
    const {
        location: { pathname },
        host,
        isAppWebviewPage,
    } = useRouter();
    const { pageType: currentPageType } = getPageTypeByUrl(pathname, host);
    const braze = useBraze();
    const { brazeReadyDebug, p100AppBanner } = useFeatureToggle();
    const logger = useLogger();

    const log = brazeReadyDebug ? (msg: string) => logger.info(`BRAZE READY DEBUG: ${msg}`) : () => null;
    const [shopToClubOverlayShownInThisSession, , cookiesEnabled] = useBellaCookies(
        SHOP_TO_CLUB_OVERLAY_SHOWN_IN_THIS_SESSION,
        true
    );
    const [sentBrazeReadyEvent, setSentBrazeReadyEvent] = useBellaCookies(SENT_BRAZE_READY_EVENT, true);
    const [smallAppBannerShownInThisSession] = useBellaCookies(SMALL_APP_BANNER_SHOWN_IN_SESSION, true);
    const waitForPossibleAppBanner = !isDesktop() && p100AppBanner === AppBannerValues.small;
    const isCartPage = currentPageType === PageType.CART || currentPageType === PageType.CART_NATIVE;
    const isWebview = !!isAppWebviewPage;

    const sendReadyEvent = React.useCallback(() => {
        if (!braze) {
            logger.error('BRAZE READY EVENT: trying to braze event but braze not there');
            return;
        }
        log('sending braze ready for overlays event');
        sendCustomEventReadyForOverlays(braze);
        setSentBrazeReadyEvent(1, oneHourTTL);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [braze]);

    React.useEffect(() => {
        const sendBrazeReadyEvent = shouldSendBrazeReadyEvent(
            {
                cookiesEnabled,
                isLoggedIn,
                braze: !!braze,
                shopToClubOverlayShownInThisSession,
                sentBrazeReadyEvent,
                smallAppBannerShownInThisSession,
                isCartPage,
                isWebview,
            },
            log
        );

        if (!sendBrazeReadyEvent || braze === null) {
            log("won't send brazy ready event");
            return () => null;
        }

        if (!waitForPossibleAppBanner) {
            sendReadyEvent();
            return () => null;
        }

        log('starting 30 sec timer to give user a chance to see small app banner');
        const timeoutID = window.setTimeout(() => {
            sendReadyEvent();
        }, 30000);

        return () => {
            window.clearTimeout(timeoutID);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isLoggedIn,
        shopToClubOverlayShownInThisSession,
        sentBrazeReadyEvent,
        cookiesEnabled,
        smallAppBannerShownInThisSession,
        braze,
        isCartPage,
        isWebview,
    ]);
};

export default useBrazeReady;
